<template>
  <div id="promotionModalEdit">
    <div class="container">

      <div class="page-title">
        <span class="name">{{ $t('promotions.modal.title') }}</span>
        <button class="save btn btn-primary" @click="save"
          v-if="$store.getters.loggedUserHasModifyPermission">>{{ $t("save") }}</button>
      </div>

      <TipBox info :title="$t('tips.promo_popup.title_2')">
        <ol>
          <li v-html="$t('tips.promo_popup.ol_1')"></li>
          <li v-html="$t('tips.promo_popup.ol_2')"></li>
          <li v-html="$t('tips.promo_popup.ol_3')"></li>
          <li v-html="$t('tips.promo_popup.ol_4')"></li>
        </ol>
      </TipBox>

      <Tabs>
        <TabSection :title="$t('promotions.modal.configuration')" icon="icon/promos.svg" collapsible>
          <TipBox info no-border>
            <span v-html="$t('tips.promo_popup.description')"></span>
          </TipBox>
          <TipBox info no-icon>
            <b v-html="$t('tips.promo_popup.description_2')"></b>
            <ul>
              <li v-html="$t('tips.promo_popup.li_1')"></li>
              <li v-html="$t('tips.promo_popup.li_2')"></li>
              <li v-html="$t('tips.promo_popup.li_3')"></li>
            </ul>
          </TipBox>

          <div class="tabs">
            <span class="tab" @click="setActiveTab(modal)" v-for="(modal, modalIndex) in modals"
              :class="{ active: activeModal === modal }">
              {{ $t("promotions.modal.title") }} #{{ modalIndex + 1 }}
              <span class="active" v-if="modal.active">{{ $t('active') }}</span>
            </span>
            <span class="tab filler"></span>
            <span class="add" @click="addNewModal"
              v-if="$store.getters.loggedUserHasModifyPermission">{{ $t('add') }}</span>
          </div>

          <template v-if="activeModal">
            <div class="activate group">
              <input id="chk_activate" type="checkbox" v-model="activeModal.active">
              <label for="chk_activate">
                {{ $t('promotions.modal.activate') }}
              </label>
            </div>

            <div class="columns">
              <div class="column">
                <Panel :title="$t('promotions.modal.window_configuration')" collapsible>
                  <div class="group">
                    <LocalizedInput :title="$t('promotions.modal.modal_title')" :max-size="50"
                      :translate="activeModal.modalTitle" :tooltip="$t('promotions.modal.modal_title_tooltip')" />
                  </div>
                  <div class="group">
                    <LocalizedInput :title="$t('promotions.modal.promo_title')" :max-size="100"
                      :translate="activeModal.promotionTitle" />
                  </div>
                  <div class="group">
                    <LocalizedInput :title="$t('promotions.modal.description')" :max-size="190" textarea
                      :translate="activeModal.description" />
                  </div>
                </Panel>
                <Panel :title="$t('promotions.modal.image_configuration')" collapsible>
                  <TipBox warning>
                    <span v-html="$t('tips.promo_popup.image')"></span>
                  </TipBox>
                  <div class="group">
                    <label>{{ $t('promotions.modal.image_position') }}</label>
                    <select v-model="activeModal.configuration.imagePosition">
                      <option value="left">{{ $t('promotions.modal.left') }}</option>
                      <option value="top">{{ $t('promotions.modal.top') }}</option>
                    </select>
                  </div>
                  <div class="group">
                    <ImageUpload identifier="uploadPromotionModalImage" :object="activeModal.image" :maxSizeInMb="0.8"
                      @changed="activeModal.image = $event" :preview-height="150"></ImageUpload>
                  </div>
                </Panel>
              </div>
              <div class="column">
                <div class="preview-title">{{ $t('promotions.modal.preview') }}</div>
                <div class="preview"
                  :class="{ imageTop: activeModal.configuration.imagePosition === 'top', imageLeft: activeModal.configuration.imagePosition === 'left' }">
                  <div class="image" :style="{ backgroundImage: imagePath(activeModal.image) }">&nbsp;</div>
                  <div class="content">
                    <div class="modal-header">
                      <svgicon src="icon/promos.svg" :height="18 < 6" :width="16"></svgicon>
                      <template v-if="activeModal.modalTitle.fr || activeModal.modalTitle.en">
                        {{ $to(activeModal.modalTitle) }}
                      </template>
                      <template v-else>{{ $t('promotions.modal.modal_title') }}</template>
                    </div>
                    <div class="content-title">
                      <template v-if="activeModal.promotionTitle.fr || activeModal.promotionTitle.en">
                        {{ $to(activeModal.promotionTitle) }}
                      </template>
                      <template v-else>{{ $t('promotions.modal.promo_title') }}</template>
                    </div>
                    <div class="content-description">
                      <template v-if="activeModal.description.fr || activeModal.description.en">
                        {{ $to(activeModal.description) }}
                      </template>
                      <template v-else>{{ $t('promotions.modal.description') }}</template>
                    </div>
                    <div class="promos">
                      <div class="promo" v-for="promotion in activeModal.promotions">
                        <div class="placeholder-image" v-if="promotion.image"
                          :style="{ backgroundImage: imagePath(promotion.image) }"></div>
                        <div class="promo-content">
                          <div class="promo-name">{{ $to(promotion.name) }}</div>
                          <div class="promo-description">{{ $to(promotion.description) }}</div>
                        </div>
                        <div class="view-promo">
                          <svgicon src="icon/arrow-right.svg" :height="22" :width="22"></svgicon>
                        </div>
                      </div>
                      <div class="promo" v-for="link in activeModal.links">
                        <div class="placeholder-image" v-show="false"></div>
                        <div class="promo-content">
                          <div class="promo-name">{{ $to(link.title) }}</div>
                          <div class="promo-description">{{ $to(link.description) }}</div>
                        </div>
                        <div class="view-promo">
                          <svgicon src="icon/arrow-right.svg" :height="22" :width="22"></svgicon>
                        </div>
                      </div>
                    </div>
                    <div class="back-to-menu">
                      <button class="btn btn-secondary">{{ $t('promotions.modal.back_to_menu') }}</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Panel :title="$t('promotions.modal.promotions_configuration')" collapsible>
              <div class="description" v-html="$t('promotions.modal.promo_description')"></div>
              <div class="available-promotions">
                <template v-if="availablePromotions.length > 0">
                  <div class="promo" v-for="promotion in availablePromotions"
                    :class="{ selected: activeModal.promotions.indexOf(promotion) > -1 }" @click="selectPromo(promotion)">
                    <div class="placeholder-image" :style="{ backgroundImage: imagePath(promotion.image) }"></div>
                    <div class="promo-content">
                      <div class="promo-name">{{ $to(promotion.name) }}</div>
                      <div class="promo-description">{{ $to(promotion.description) }}</div>
                    </div>
                    <div class="select-promo">
                      <div class="checkbox"></div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="empty">{{ $t('promotions.modal.no_promo') }}</div>
                </template>
              </div>
            </Panel>

            <Panel v-if="activeModal" class="modal-links" :title="$t('promotions.modal.links_configuration')" collapsible>
              <div class="description">
                <span v-html="$t('promotions.modal.links_description')"></span>
                <button class="btn btn-primary" @click="addNewLink"
                  v-if="$store.getters.loggedUserHasModifyPermission">{{ $t('add') }}</button>
              </div>
              <div class="links">
                <div class="link" v-for="(link, linkIndex) of activeModal.links" :key="'link' + linkIndex">
                  <div class="columns">
                    <div class="column">
                      <div class="group">
                        <LocalizedInput :title="$t('promotions.modal.link_title')" :max-size="50"
                          :translate="link.title" />
                      </div>
                      <div class="group">
                        <LocalizedInput :title="$t('promotions.modal.link_description')" :max-size="100" textarea
                          :translate="link.description" />
                      </div>
                    </div>
                    <div class="column">
                      <div class="group">
                        <label><b>{{ $t('promotions.modal.url_type') }}</b></label>
                        <input type="radio" :id="'chk_internal' + linkIndex" :value="true" v-model="link.isInternal">
                        <label :for="'chk_internal' + linkIndex">{{ $t('promotions.modal.link_is_internal') }}</label>
                        <input type="radio" :id="'chk_external' + linkIndex" :value="false" v-model="link.isInternal">
                        <label :for="'chk_external' + linkIndex">{{ $t('promotions.modal.link_is_external') }}</label>
                      </div>
                      <div class="group" v-if="link.isInternal">
                        <label>{{ $t('promotions.modal.destination') }}</label>
                        <select v-model="link.url">
                          <option value="" selected disabled hidden>{{ $t('choose') }}...</option>
                          <option value="/item/">{{ $t('promotions.modal.url.item') }}</option>
                          <option value="/menu/">{{ $t('promotions.modal.url.category') }}</option>
                          <option value="/promotions">{{ $t('promotions.modal.url.promotions') }}</option>
                          <option value="/menu">{{ $t('promotions.modal.url.menu') }}</option>
                        </select>
                      </div>
                      <div class="group" v-if="link.url === '/item/'">
                        <label>{{ $t('promotions.modal.select_item') }}</label>
                        <AjaxSelect v-if="inventory" v-model="link.item" api="/api/entity/item/select"
                          :query="{ inventory: inventory.id }" :map-fn="(i) => i.name" />
                      </div>
                      <div class="group" v-if="link.url === '/menu/'">
                        <label>{{ $t('promotions.modal.select_category') }}</label>
                        <AjaxSelect v-if="inventory" v-model="link.category" api="/api/entity/category/select"
                          :query="{ inventory: inventory.id }" :map-fn="(i) => i.name" />
                      </div>
                      <div class="group" v-if="link.isInternal === false">
                        <label>{{ $t('promotions.modal.link_url') }}</label>
                        <input type="text" v-model="link.url">
                      </div>
                      <div class="delete-holder">
                        <button class="btn btn-secondary" @click="deleteLink(linkIndex)">
                          {{ $t('promotions.modal.delete_link') }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <template v-if="activeModal.links.length === 0">
                  <div class="empty">{{ $t('promotions.modal.no_link') }}</div>
                </template>
              </div>
            </Panel>

            <Panel class="companies" :title="$t('promotions.companies')" v-if="branchCompanies.length > 0 && !companyId"
              collapsible>
              <div class="description">{{ $t('promotions.companies_description') }}</div>
              <ListMultiSelect :bank="branchCompanies" :selected="activeModal.companies" show-select-all show-unselect-all
                :bank-title="$t('promotions.branch_companies').replace('{INVENTORY}', $to($store.state.contextInventory.name))"
                :selected-title="$t('promotions.selected_branch_companies')"></ListMultiSelect>
            </Panel>

            <div class="buttons inside-content" v-if="$store.getters.loggedUserHasModifyPermission">
              <button class="btn btn-secondary" @click="deleteModal">{{ $t('promotions.modal.delete') }}</button>
            </div>
          </template>
        </TabSection>
      </Tabs>

      <div class="footer-actions" v-if="$store.getters.loggedUserHasModifyPermission">
        <button class="btn btn-primary" @click="save">{{ $t('save') }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import LocalizedInput from "@/components/tools/LocalizedInput.vue";
  import ListMultiSelect from "@/components/tools/ListMultiSelect.vue";
  import ImageUpload from '@/components/tools/ImageUpload.vue';
  import Tooltip from '@/components/tools/Tooltip.vue';
  import AjaxSelect from "@/components/tools/AjaxSelect.vue";
  import { Core } from "@/core/Core";
  import { mapActions } from 'vuex';

  export default {
    name: "promotionModalEdit",

    components: { LocalizedInput, ImageUpload, Tooltip, AjaxSelect, ListMultiSelect },

    data() {
      return {
        active: false,
        inventory: null,
        id: null,
        activeModal: null,
        availablePromotions: [],
        branchCompanies: [],
        modals: [],
        deletedIds: []
      }
    },

    computed: {
      branchId() {
        return this.$route.query.branch;
      },
      companyId() {
        return this.$route.query.company;
      },
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "promotions.modal.title";
      this.$store.state.pageIcon = "/icon/promos.svg";
      this.inventory = await this.$store.getters.getContextInventory;
    },

    watch: {
      inventory: {
        immediate: true,
        async handler(inventory) {
          if (!inventory) {
            return;
          }
          await this.loadBranchCompanies();
          let modals = await this.getPromotionModals({ inventory: inventory.id });
          let promotions = await this.getVisiblePromotions({ inventory: inventory.id, branch: this.branchId, company: this.companyId });
          if (modals && promotions) {
            this.availablePromotions = promotions;
            this.loadData(modals.data);
            this.loadFirstModal();
          }
        }
      },
      activeModal: {
        deep: true,
        handler: function (newValue) {
          if (!newValue) {
            return;
          }
          for (let link of newValue.links) {
            let internalURLs = ["/item/", "/menu/", "/menu", "promotions"];
            if (!link.isInternal && internalURLs.indexOf(link.url) > -1) {
              link.url = "";
            }
            switch (link.url) {
              case "/item/":
                if (link.category.id) {
                  link.category = {};
                }
                break;
              case "/menu/":
                if (link.item.id) {
                  link.item = {};
                }
                break;
              default:
                if (link.item.id) {
                  link.item = {};
                }
                if (link.category.id) {
                  link.category = {};
                }
                break;
            }
          }
        }
      }
    },

    methods: {
      ...mapActions(['getVisiblePromotions', 'getPromotionModals', 'savePromotionModal', 'createPromotionModal', 'deletePromotionModal']),

      setActiveTab(modal) {
        this.activeModal = modal;

        if (!this.$store.getters.loggedUserHasModifyPermission) {
          window.lockFields();
        }
      },

      loadFirstModal() {
        if (this.modals.length === 0) {
          this.addNewModal();
        } else {
          this.setActiveTab(this.modals[0]);
        }
      },

      async loadBranchCompanies() {
        if (this.companyId) {
          this.branchCompanies = [];
        } else {
          this.branchCompanies = await Core.request('GET', '/api/entity/company/select', { branch: this.branchId });
          if (this.branchCompanies.length > 0) {
            this.branchCompanies = this.branchCompanies.filter(c => c.inventory == this.inventory.id);
          }
        }
      },

      addNewModal() {
        this.modals.push({
          image: { url: null },
          modalTitle: { fr: "", en: "" },
          promotionTitle: { fr: "", en: "" },
          description: { fr: "", en: "" },
          promotions: [],
          companies: [],
          links: [],
          configuration: {
            imagePosition: 'left'
          }
        });
        this.setActiveTab(this.modals[this.modals.length - 1]);
      },

      addNewLink() {
        this.activeModal.links.push({
          title: { fr: "", en: "" },
          description: { fr: "", en: "" },
          url: "",
          item: {},
          category: {},
          isInternal: null
        })
      },

      deleteLink(index) {
        this.activeModal.links.splice(index, 1);
      },

      loadData(modals) {
        this.modals = [];
        for (let modalData of modals) {
          let modal = {};
          modal.id = modalData.id;
          modal.active = modalData.active || false;
          modal.modalTitle = modalData.modalTitle || { fr: "", en: "" };
          modal.promotionTitle = modalData.promotionTitle || { fr: "", en: "" };
          modal.description = modalData.description || { fr: "", en: "" };
          modal.configuration = modalData.configuration || { imagePosition: 'left' };
          modal.image = modalData.image || { url: "" };
          modal.links = modalData.links || [];
          modal.companies = modalData.companies || [];
          modal.promotions = [];
          for (let promotionId of modalData.promotions) {
            let foundPromotion = this.availablePromotions.find(p => p.id === promotionId);
            if (foundPromotion) {
              modal.promotions.push(foundPromotion);
            }
          }
          if (!this.companyId) {
            modal.companies = this.branchCompanies.filter(c => modal.companies.indexOf(c.id) > -1);
          }
          this.modals.push(modal);
        }
      },

      getDataBeforeSave() {
        let data = [];
        let modalsData = JSON.parse(JSON.stringify(this.modals));
        for (let modal of modalsData) {
          data.push({
            id: modal.id ? modal.id : undefined,
            active: modal.active,
            image: getImageData(modal.image),
            modalTitle: modal.modalTitle,
            promotionTitle: modal.promotionTitle,
            description: modal.description,
            configuration: modal.configuration,
            companies: modal.companies.map(c => c.id),
            links: modal.links,
            promotions: modal.promotions.map(p => p.id),
            inventory: this.inventory.id,
          })
        }
        return data;
      },

      validateBeforeSave() {
        let valid = true;
        let activeModals = this.modals.filter(m => m.active);
        if (this.companyId) {
          /* COMPANY LEVEL - Only 1 active pop-up allowed */
          if (activeModals.length > 1) {
            toast(this.$t("promotions.modal.enable_warning"), "warning");
            valid = false;
          }
        } else {
          /* BRANCH LEVEL - Only 1 active pop-up by company allowed */
          let companiesAssigned = [];
          for (let modal of activeModals) {
            for (let company of modal.companies) {
              let companyAlreadyAssigned = companiesAssigned.find(c => c === company.id);
              if (companyAlreadyAssigned) {
                toast(this.$t("promotions.modal.enable_warning_company").replace("{COMPANY}", company.name), "warning");
                valid = false;
                break;
              } else {
                companiesAssigned.push(company.id);
              }
            }
          }
        }
        return valid;
      },

      async save() {
        if (!this.validateBeforeSave()) {
          return;
        }
        this.$store.commit("showSpinner", true);
        let toSaveModals = this.getDataBeforeSave();
        let savedModals = [];
        for (let i = 0; i < toSaveModals.length; i++) {
          let data = toSaveModals[i];
          let response = data.id
            ? await this.savePromotionModal({ id: data.id, data })
            : await this.createPromotionModal({ data });
          if (response) {
            savedModals.push(response);
          }
        }
        this.loadData(savedModals);
        this.loadFirstModal();

        /* delete old ones */
        for (let id of this.deletedIds) {
          await this.deletePromotionModal({ id });
        }
        this.deletedIds = [];

        this.$store.commit("showSpinner", false);

        if (savedModals.length === toSaveModals.length) {
          toast(this.$t("promotions.modal.success"), "success");
        } else {
          toast(this.$t("promotions.modal.error"), "error");
        }
      },

      selectPromo(promotion) {
        let index = this.activeModal.promotions.indexOf(promotion);
        if (index > -1) {
          this.activeModal.promotions.splice(index, 1);
        } else {
          if (this.activeModal.promotions.length === 4) {
            toast(this.$t("promotions.modal.max_reached"), "warning");
            return;
          }
          this.activeModal.promotions.push(promotion);
        }
      },

      deleteModal() {
        if (this.activeModal.id) {
          this.deletedIds.push(this.activeModal.id)
        }
        let index = this.modals.indexOf(this.activeModal);
        this.modals.splice(index, 1);
        this.setActiveTab(this.modals[0] || null);
      },

      imagePath(image) {
        if (image && image.url) {
          let path = image.url.startsWith("data:") ? image.url : Parameters.urlFileServer + image.url;
          return "url('" + path + "')";
        } else {
          return "none";
        }
      },
    }
  }
</script>

<style lang="scss" scoped>
  .preview-title {
    text-align: center;
    font-weight: bold;
    font-size: 20px;
    color: #5e5e5e;
  }

  .description {
    margin-bottom: 15px;
  }

  .preview {
    background-color: #f8f8f8;
    border-radius: 5px;
    margin-top: 20px;
    display: flex;
    box-shadow: 0px 0px 6px 1px rgba(0, 0, 0, 0.1);
    margin-left: auto;
    margin-right: auto;

    &.imageLeft {
      max-width: 700px;

      .image {
        width: 200px;
      }
    }

    &.imageTop {
      flex-direction: column;
      max-width: 500px;

      .image {
        height: 400px;
        border-top-left-radius: 5px;
        border-top-right-radius: 5px;
      }

      .modal-header {
        display: none;
      }
    }

    .image {
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      flex-shrink: 0;
    }

    .content-title {
      font-size: 22px;
      font-weight: bold;
      margin-bottom: 10px;
    }

    .content-description {
      font-size: 18px;
      margin-bottom: 15px;
    }

    .promotions {
      border-top: 1px dashed #ccc;
    }

    .content {
      padding: 15px;
      flex-grow: 1;
    }

    .modal-header {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 15px;
      padding-bottom: 10px;
      border-bottom: 1px solid #ccc;
      display: flex;
      align-items: center;

      .svgicon {
        margin-right: 10px;
        fill: #ccc;
      }
    }

    .back-to-menu {
      display: flex;
      justify-content: flex-end;
      padding-top: 15px;
      margin-top: 15px;
      border-top: 1px dashed #ccc;
    }

    .btn {
      background-color: #ccc;
      color: #5e5e5e;
    }

  }

  .image,
  .placeholder-image {
    background-size: cover;
    background-position: center;
  }

  .promo {
    display: flex;
    border: 1px solid #ccc;
    border-radius: 5px;
    min-height: 80px;

    &:not(:last-child) {
      margin-bottom: 10px;
    }

    .promo-content {
      padding: 10px;
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .promo-name {
      font-weight: bold;
      margin-bottom: 5px;
    }

    .promo-description {
      font-size: 14px;
      color: #5c5c5c;
    }

    .placeholder-image {
      width: 80px;
      flex-shrink: 0;
    }

    .view-promo {
      fill: #aaa;
    }
  }

  .available-promotions {
    display: flex;
    flex-wrap: wrap;

    .promo {
      width: 30%;
      margin-right: 20px;
      cursor: pointer;
    }
  }

  .promo.selected {
    border-color: var(--primary-color);
  }

  .select-promo,
  .view-promo {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 50px;
  }

  .checkbox {
    height: 20px;
    width: 20px;
    outline: 1px solid #aaa;
    border: 3px solid white;
  }

  .selected .checkbox {
    outline-color: var(--primary-color);
    background-color: var(--primary-color);
  }

  .buttons {
    width: 100%;
    text-align: center;
    padding-top: 20px;
    border-top: 1px solid #ccc;

    &.inside-content {
      text-align: right;
    }
  }

  .activate {
    margin: 25px 0;
    font-weight: bold;

    label {
      color: inherit;
    }
  }

  .empty {
    font-style: italic;
    font-size: 14px;
    margin-top: 20px;
  }

  .tabs {
    .tab {
      flex-grow: 0;
      cursor: pointer;
      white-space: nowrap;

      &.filler {
        flex-grow: 1;
        pointer-events: none;
      }

      .active {
        background-color: var(--primary-color);
        color: white;
        padding: 4px 8px;
        border-radius: 5px;
        font-size: 10px;
        margin-left: 10px;
      }
    }

    .add {
      background-color: var(--primary-color);
      border-radius: 4px;
      color: white;
      padding: 8px 16px;
      margin-top: 15px;
      margin-left: 10px;
      cursor: pointer;
    }
  }

  .modal-links {
    .description {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .link {
      border: 1px solid #ccc;
      background-color: #fbfbfb;
      border-radius: 5px;
      padding: 15px 15px 0 15px;

      &:not(:last-child) {
        margin-bottom: 15px;
      }
    }

    .column {
      display: flex;
      flex-direction: column;
    }

    .delete-holder {
      flex-grow: 1;
      padding-bottom: 15px;
      display: flex;
      align-items: flex-end;
      justify-content: flex-end;
    }
  }

  @media (max-width: 1400px) {
    .available-promotions .promo {
      width: 46%;
    }
  }

  @media (max-width: 800px) {
    .available-promotions .promo {
      width: 100%;
      margin-right: 0;
    }
  }
</style>
